import React, { useState } from 'react';
import { Box } from '@mui/material';
import HeroCard from '../components/HeroCard';
import BridgeForm from '../components/BridgeForm';
import useBridgeChains from '../hooks/useBridgeChains';
import useTokenBalance from '../hooks/useTokenBalance';
import { recordHistoryEntry } from '../lib/localHistory';

// Landing page = hero + form. The form builds the approve()+requestSwap()
// calldata itself (no server call — see components/BridgeForm.jsx) and hands
// a locally-generated plan up here; we stash it in sessionStorage so the
// status page can sign without rebuilding anything, and navigate to
// /bridge/<localId>.

const INTENT_STASH_KEY = 'bridge.lastIntent.v1';

export default function BridgeLanding({ onSubmitted }) {
  const { pairs, loading } = useBridgeChains();
  const [pair, setPair] = useState({ src: null, dst: null, symbol: 'kUSD' });

  // The matching route object (kStables address map) for the currently
  // selected pair/symbol — used to pass an explicit token address into
  // useTokenBalance rather than the stale shared STATIC chains.js map (that
  // map has no kUSD entry for Base/Arbitrum; GET /bridge/routes is the
  // live-checked source of truth for these addresses).
  const activeRoute = pairs.find((p) => p.src.id === pair.src && p.dst.id === pair.dst);
  const kInfo = activeRoute?.kStables?.[pair.symbol] ?? null;

  // Live ERC20 balance for the source chain (drives the Max button +
  // over-balance error). null = unknown / no wallet / not deployed.
  const sourceBalance = useTokenBalance(pair.src, pair.symbol, 0, kInfo?.srcKStableAddress);
  const destBalance = useTokenBalance(pair.dst, pair.symbol, 0, kInfo?.dstKStableAddress);

  const handleIntent = (intentId, intentData) => {
    try {
      if (intentData) {
        sessionStorage.setItem(
          INTENT_STASH_KEY,
          JSON.stringify({ intentId, intentData, savedAt: Date.now() }),
        );
      }
    } catch (_) {
      // sessionStorage may be blocked — the status page recovers what it can
      // from on-chain reads alone in that case.
    }
    if (intentData) {
      recordHistoryEntry({
        localId: intentId,
        srcChainId: intentData.srcChainId,
        dstChainId: intentData.dstChainId,
        kStableSymbol: intentData.kStableSymbol,
        amountWei: intentData.amountWei,
        netWei: intentData.netWei,
        recipient: intentData.recipient,
        dstBridgeReceiverAddress: intentData.dstBridgeReceiverAddress,
        requestedAt: Math.floor((intentData.createdAt || Date.now()) / 1000),
        state: 'intent_created',
      });
    }
    onSubmitted?.(intentId);
  };

  return (
    <Box>
      <HeroCard />
      <BridgeForm
        pairs={pairs}
        pairsLoading={loading}
        onIntent={handleIntent}
        onPairChanged={(src, dst, symbol) => setPair({ src, dst, symbol })}
        sourceBalance={sourceBalance}
        destBalance={destBalance}
      />
    </Box>
  );
}

export { INTENT_STASH_KEY };
